import React, { useMemo, useRef, useEffect } from "react";
import * as Slider from "./styles/ButtonSliderStyles";
import type {
    SliderValueOrderType,
    SliderButtonType,
} from "../../../../../../shared-logic/features/filters/utils/constants/filterConfigConstants";
import useButtonSlider from "../../../../../../shared-logic/features/filters/hooks/useButtonSlider";

type ButtonSliderType = {
    order: SliderValueOrderType;
    minValue: number;
    maxValue: number;
    enableMinValue?: boolean;
    currentMinValue?: number;
    currentMaxValue: number;
    step: number;
    setValue: (value: number, type: SliderButtonType) => void;
    getValueLabel?: (max: number, min: number) => string;
    setValueLabel?: (label: string) => void;
    getSecondaryValueLabel?: (max: number, min: number) => string; // Used for secondary price in dual currency
    setSecondaryValueLabel?: (label: string) => void; // Used for secondary price in dual currency
};

const ButtonSlider = (props: ButtonSliderType): JSX.Element => {
    const { enableMinValue, minValue, maxValue, step, order, currentMaxValue, currentMinValue } = props;
    const { setValue, getValueLabel, setValueLabel, getSecondaryValueLabel, setSecondaryValueLabel } = props;

    const sliderBarRef = useRef<HTMLDivElement>(null);
    const { getValueFromXPos, getOnMouseDown, getOnTouchStart, getOnKeyDown, xPositionLeft, xPositionRight } =
        useButtonSlider(
            sliderBarRef,
            order,
            maxValue,
            minValue,
            step,
            setValue,
            currentMaxValue,
            currentMinValue,
            Slider.buttonSize,
        );

    const valueLabel =
        getValueLabel && !setValueLabel
            ? getValueLabel(getValueFromXPos(xPositionRight), enableMinValue ? getValueFromXPos(xPositionLeft) : 0)
            : "";

    useEffect(() => {
        // Update value label
        if (setValueLabel && getValueLabel) {
            setValueLabel(
                getValueLabel(getValueFromXPos(xPositionRight), enableMinValue ? getValueFromXPos(xPositionLeft) : 0),
            );
        }
        // Update secondary value label (dual currency)
        if (setSecondaryValueLabel && getSecondaryValueLabel) {
            setSecondaryValueLabel(
                getSecondaryValueLabel(
                    getValueFromXPos(xPositionRight),
                    enableMinValue ? getValueFromXPos(xPositionLeft) : 0,
                ),
            );
        }
    }, [currentMinValue, currentMaxValue, xPositionRight, xPositionLeft, enableMinValue]);

    return useMemo(
        () => (
            <>
                {valueLabel && (
                    <Slider.Value>
                        <span dangerouslySetInnerHTML={{ __html: valueLabel }} />
                    </Slider.Value>
                )}
                <Slider.default ref={sliderBarRef}>
                    {enableMinValue && (
                        <Slider.Handle
                            onMouseDown={getOnMouseDown("min")}
                            onTouchStart={getOnTouchStart("min")}
                            onKeyDown={getOnKeyDown("min")}
                            left={xPositionLeft}
                        />
                    )}
                    <Slider.Track
                        left={`${enableMinValue ? xPositionLeft : -Slider.buttonSize / 2}px`}
                        width={`${xPositionRight - xPositionLeft + (enableMinValue ? 0 : Slider.buttonSize / 2)}px`}
                    />
                    <Slider.Handle
                        onMouseDown={getOnMouseDown("max")}
                        onTouchStart={getOnTouchStart("max")}
                        onKeyDown={getOnKeyDown("max")}
                        left={xPositionRight}
                    />
                </Slider.default>
            </>
        ),
        [enableMinValue, getOnMouseDown, getOnTouchStart, valueLabel, xPositionLeft, xPositionRight],
    );
};

export default ButtonSlider;
